/**
 * GlossaryTerm — surligne un terme technique dans le texte et affiche sa
 * définition au survol (tooltip pédagogique cliquable).
 *
 * 11/02/2026 (Phase 2 — Pédagogie apprenant).
 *
 * Usage :
 *   <GlossaryTerm conceptId="amortissement">amortissement</GlossaryTerm>
 *   <GlossaryTerm conceptId="is_impot" label="IS">l'IS</GlossaryTerm>
 *
 * Le composant lit la définition dans data/pedagogyConcepts.js et affiche
 * un underline pointillé + tooltip riche au hover.
 *
 * Design volontairement léger — pas d'accessibilité JAWS complète pour v1
 * (title=… fait déjà 80% du boulot). Peut être upgradé avec Radix Tooltip
 * plus tard si besoin.
 */
import React, { useState, useRef, useEffect } from 'react';
import { GraduationCap } from 'lucide-react';
import { CONCEPT_BY_ID } from '../data/pedagogyConcepts';

export default function GlossaryTerm({ conceptId, children, label = null, className = '' }) {
  const concept = CONCEPT_BY_ID[conceptId];
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Ferme le popover si on clique en dehors.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!concept) {
    // Concept introuvable — on rend juste le texte sans style, pas de crash.
    return <span className={className}>{children}</span>;
  }

  const displayLabel = label || children || concept.label;

  return (
    <span ref={wrapperRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className={`inline-flex items-center gap-0.5 border-b border-dotted border-accent/60 text-accent hover:text-accent/80 focus:outline-none focus:ring-1 focus:ring-accent/40 rounded-sm ${className}`}
        title={concept.definition}
        data-testid={`glossary-term-${concept.id}`}
        aria-label={`${concept.label} — voir la définition`}
      >
        {displayLabel}
      </button>
      {open && (
        <span
          className="absolute z-[9990] bottom-full left-1/2 -translate-x-1/2 mb-1 w-72 bg-ink-800 border border-ink-600 rounded shadow-2xl p-2.5 text-xs text-slate-300 leading-relaxed text-left pointer-events-none"
          role="tooltip"
          data-testid={`glossary-popup-${concept.id}`}
        >
          <span className="flex items-center gap-1 mb-1">
            <GraduationCap className="w-3 h-3 text-accent" />
            <span className="text-[10px] uppercase tracking-widest font-bold text-accent">
              {concept.category} · {concept.label}
            </span>
          </span>
          <span className="block">{concept.definition}</span>
        </span>
      )}
    </span>
  );
}
